/**
 * Content for the Timeline's right-side "alert context" panel — TimelineView
 * owns the panel shell (slide animation, ✕ button); this just renders one
 * selected incident's detail. Shared by both the single-product Timeline
 * (ProductAlertsSection) and the cross-product one (AlertsListPage) so a
 * selected bar looks the same everywhere.
 *
 * Kept intentionally compact: icon+name header (type/severity color-coded
 * to match the product detail page's active-alert tiles, with the detail
 * moved to the icon's tooltip instead of taking its own rows), code, a
 * single opened–closed time range, duration, and a status chip.
 */
import * as React from "react";
import { AlertTriangle, ArrowRight, Bell } from "lucide-react";
import { Button } from "../../../ui/components/Button";
import { Text } from "../../../ui/components/Text";
import { alertTypeColor, formatAlertDuration, severityLabel } from "./alertDisplay";
import { DismissAlertModal } from "./DismissAlertModal";
import { PulsingDot } from "./PulsingDot";
import * as s from "./AlertContextPanelContent.css";

export interface AlertContextPanelAlert {
  incidentId: string;
  code: string;
  name: string;
  type: string | null;
  severity: number | null;
  description: string | null;
  openedAt: string;
  closedAt: string | null;
  active: boolean;
  resolvedSource: "device_event" | "stale_expired" | "manual_dismiss" | null;
  resolvedByUserName: string | null;
  resolutionNote: string | null;
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" });
  } catch {
    return iso;
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export function AlertContextPanelContent({
  alert,
  canDismiss = false,
  onDismiss,
}: {
  alert: AlertContextPanelAlert;
  /** Whether the current user has permission to dismiss this alert's product. */
  canDismiss?: boolean;
  onDismiss?: (reason: string) => Promise<void>;
}) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [dismissing, setDismissing] = React.useState(false);
  const [dismissError, setDismissError] = React.useState<string | null>(null);

  const Icon = alert.type === "fault" ? AlertTriangle : Bell;
  // Same color the bar itself uses (getColor: r => alertTypeColor(r.type)) —
  // severity is still surfaced, just via the tooltip instead of the color,
  // so the icon always matches the bar it came from.
  const color = alertTypeColor(alert.type);
  const tooltip = `Type: ${alert.type === "fault" ? "Fault" : "Alarm"} · Severity: ${severityLabel(alert.severity)}`;

  const openedMs = new Date(alert.openedAt).getTime();
  const closedMs = alert.active ? Date.now() : new Date(alert.closedAt ?? alert.openedAt).getTime();

  const isDismissed = !alert.active && alert.resolvedSource === "manual_dismiss";
  const statusLabel = alert.active ? "Ongoing" : isDismissed ? "Dismissed" : "Closed";
  const statusClass = alert.active
    ? s.statusChipOngoing
    : isDismissed
      ? s.statusChipDismissed
      : s.statusChipClosed;

  const handleConfirmDismiss = async (reason: string) => {
    if (!onDismiss) return;
    setDismissing(true);
    setDismissError(null);
    try {
      await onDismiss(reason);
      setModalOpen(false);
    } catch (err) {
      setDismissError(err instanceof Error ? err.message : "Failed to dismiss alert");
    } finally {
      setDismissing(false);
    }
  };

  return (
    <div className={s.root}>
      <div className={s.header}>
        <span className={s.iconCircle} style={{ color }} title={tooltip}>
          {alert.active && <span className={s.iconPulseRing} />}
          <Icon size={20} strokeWidth={2.25} aria-hidden className={s.iconGlyph} />
        </span>
        <div className={s.headerText}>
          <Text level="body-small" weight="bold" className={s.name}>
            {alert.name}
          </Text>
          <div className={s.codeRow}>
            <span className={s.codeBox}>{alert.code}</span>
            <span className={[s.statusChip, statusClass].join(" ")}>
              <PulsingDot color="currentColor" active={alert.active} size={6} />
              {statusLabel}
            </span>
          </div>
        </div>
      </div>

      <div className={s.timesRow}>
        <div className={s.timeCard}>
          <span className={s.timeCardTime}>{formatTime(alert.openedAt)}</span>
          <span className={s.timeCardDate}>{formatDate(alert.openedAt)}</span>
        </div>
        <ArrowRight size={14} className={s.timeArrow} aria-hidden />
        <div className={s.timeCard}>
          {alert.active ? (
            <span className={s.timeCardOngoing}>Ongoing</span>
          ) : (
            <>
              <span className={s.timeCardTime}>{formatTime(alert.closedAt)}</span>
              <span className={s.timeCardDate}>{formatDate(alert.closedAt)}</span>
            </>
          )}
        </div>
      </div>
      <div className={s.duration}>{formatAlertDuration(openedMs, closedMs)}</div>

      {isDismissed && (alert.resolutionNote || alert.resolvedByUserName) && (
        <div className={s.dismissNote}>
          {alert.resolvedByUserName && (
            <strong className={s.dismissNoteBy}>Dismissed by {alert.resolvedByUserName}</strong>
          )}
          {alert.resolutionNote && <p className={s.dismissNoteReason}>{alert.resolutionNote}</p>}
        </div>
      )}

      {alert.active && canDismiss && onDismiss && (
        <Button
          color="danger"
          variant="outline"
          size="sm"
          className={s.dismissButton}
          onClick={() => setModalOpen(true)}
        >
          Dismiss
        </Button>
      )}

      {onDismiss && (
        <DismissAlertModal
          open={modalOpen}
          alertName={alert.name}
          alertCode={alert.code}
          loading={dismissing}
          error={dismissError}
          onCancel={() => {
            if (!dismissing) {
              setModalOpen(false);
              setDismissError(null);
            }
          }}
          onConfirm={handleConfirmDismiss}
        />
      )}
    </div>
  );
}
